import React, { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark';
type ThemePreference = 'light' | 'dark' | 'system';

interface ThemeContextType {
  theme: Theme;
  themePreference: ThemePreference;
  toggleTheme: () => void;
  setThemePreference: (preference: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Get system theme
  const getSystemTheme = (): Theme => {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  };

  // Initialize theme preference from localStorage or default to system
  const [themePreference, setThemePreference] = useState<ThemePreference>(() => {
    const savedPreference = localStorage.getItem('themePreference') as ThemePreference;
    return savedPreference || 'system';
  });

  // Calculate actual theme based on preference
  const [theme, setTheme] = useState<Theme>(() => {
    const preference = localStorage.getItem('themePreference') as ThemePreference || 'system';
    
    if (preference === 'system') {
      return getSystemTheme();
    }
    return preference as Theme;
  });

  // Update theme when preference changes
  useEffect(() => {
    localStorage.setItem('themePreference', themePreference);
    
    if (themePreference === 'system') {
      setTheme(getSystemTheme());
    } else {
      setTheme(themePreference as Theme);
    }
  }, [themePreference]);

  // Update document class when theme changes
  useEffect(() => {
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(theme);
  }, [theme]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      // Only update if using system preference
      if (themePreference === 'system') {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [themePreference]);

  const toggleTheme = () => {
    // Simply toggle between light and dark (override system preference)
    setThemePreference(theme === 'light' ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ theme, themePreference, toggleTheme, setThemePreference }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}; 